import OfficeBuilding from "@assets/images/office-building.svg";
import { Box, Grid } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import SingleAutocomplete from "@saleor/components/SingleAutocomplete";
import TextInputDisable from "@saleor/components/TextInputDisable";
import { orderTypeChoices } from "@saleor/createOrderExport/components/AgencyTab/consts";
import useAgency from "@saleor/createOrderExport/views/CreateOrderView/useAgency";
import {
  ExportOrderWithAllItemsQuery,
  ScgExportOrderType
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { useFormik } from "formik";
import React, { useEffect, useMemo } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    root: {},
    header: {
      display: "flex",
      margin: "1.8rem 0",
      alignItems: "center",
      color: theme.palette.grey[900],
      fontSize: "1.8rem"
    },
    svg: {
      marginRight: "1.2rem"
    },
    box: {
      [theme.breakpoints.up("md")]: {
        maxWidth: "568px"
      },
      background: "#FFFFFF",
      padding: "2.4rem",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      borderRadius: "12px"
    },
    selected: {},
    rightGrid: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      paddingRight: "2.4rem"
    },
    piNo: {
      background: theme.palette.background.default,
      borderRadius: "4px",
      padding: "0.9rem 0 0.9rem 1.6rem"
    },
    selectField: {
      "& .MuiSelect-root": {
        padding: "0.8rem 1.2rem",
        "&>span>span": {
          fontWeight: 400,
          lineHeight: "2.2rem"
        }
      }
    },
    selectItem: {
      fontWeight: "bold",
      display: "inline-block",
      marginRight: 5
    }
  }),
  {
    name: "AgencyTab"
  }
);

interface AgencyProps {
  dataExportOrder?: ExportOrderWithAllItemsQuery["exportOrderWithAllItems"];
  saveDraft?: number;
  submitOrderCall?: number;
  setCollectDataSubmit?: (value) => void;
}

const AgencyTab: React.FC<AgencyProps> = ({
  dataExportOrder,
  saveDraft,
  submitOrderCall,
  setCollectDataSubmit
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const formik = useFormik({
    initialValues: {
      salesOfficeId:
        dataExportOrder?.salesOffice?.id ||
        dataExportOrder?.pi?.salesOffice?.id ||
        "",
      salesOrganizationId:
        dataExportOrder?.salesOrganization?.id ||
        dataExportOrder?.pi?.salesOrganization?.id ||
        "",
      salesGroupId:
        dataExportOrder?.salesGroup?.id ||
        dataExportOrder?.pi?.salesGroup?.id ||
        "",
      distributionChannelId:
        dataExportOrder?.distributionChannel?.id ||
        dataExportOrder?.pi?.distributionChannel?.id ||
        "",
      divisionId:
        dataExportOrder?.division?.id ||
        dataExportOrder?.pi?.division?.id ||
        "",
      orderType: dataExportOrder?.orderType || ScgExportOrderType.ZOR
    },
    onSubmit: () => null
  });

  const salesOrganizationCode = useMemo(() => {
    try {
      if (saleOrganizationChoices?.length) {
        const salesOrgan = saleOrganizationChoices.find(
          item => item.value === formik.values.salesOrganizationId
        );
        return salesOrgan?.code;
      } else {
        return dataExportOrder?.salesOrganization?.code;
      }
    } catch (e) {
      return dataExportOrder?.salesOrganization?.code;
    }
  }, [formik.values.salesOrganizationId]);

  const [
    saleGroupChoices,
    divisionChoices,
    saleOfficeChoices,
    _,
    saleOrganizationChoices
  ] = useAgency(salesOrganizationCode);

  const distributionChannelChoicesFinal = dataExportOrder?.distributionChannel
    ?.id
    ? [
        {
          value: dataExportOrder?.distributionChannel?.id,
          label: `${dataExportOrder?.distributionChannel?.code} - ${dataExportOrder?.pi?.distributionChannel?.name}`
        }
      ]
    : [
        {
          value: dataExportOrder?.pi?.distributionChannel?.id,
          label: `${dataExportOrder?.pi?.distributionChannel?.code} - ${dataExportOrder?.pi?.distributionChannel?.name}`
        }
      ];

  useEffect(() => {
    if (saleGroupChoices && saleOfficeChoices.length) {
      if (!formik.values.salesOfficeId) {
        formik.setFieldValue(
          "salesOfficeId",
          saleOfficeChoices.length ? saleOfficeChoices[0].value : ""
        );
      }
      if (!formik.values.salesOrganizationId) {
        formik.setFieldValue(
          "salesOrganizationId",
          saleOrganizationChoices.length ? saleOrganizationChoices[0].value : ""
        );
      }
      if (!formik.values.salesGroupId) {
        formik.setFieldValue(
          "salesGroupId",
          saleGroupChoices.length ? saleGroupChoices[0].value : ""
        );
      }
      if (!formik.values.distributionChannelId) {
        formik.setFieldValue(
          "distributionChannelId",
          distributionChannelChoicesFinal.length
            ? distributionChannelChoicesFinal[0].value
            : ""
        );
      }
      if (!formik.values.divisionId) {
        formik.setFieldValue(
          "divisionId",
          divisionChoices.length ? divisionChoices[0].value : ""
        );
      }
    }
  }, [saleGroupChoices]);

  useEffect(() => {
    if (saveDraft) {
      setCollectDataSubmit(formik.values);
    }
  }, [saveDraft]);

  useEffect(() => {
    if (submitOrderCall) {
      setCollectDataSubmit(formik.values);
    }
  }, [submitOrderCall]);

  return (
    <div className={classes.root}>
      <MyTypography variant="body1" className={classes.header}>
        <SVG
          width={24}
          height={24}
          className={classes.svg}
          src={OfficeBuilding}
        />
        <b>{intl.formatMessage({ defaultMessage: "ข้อมูลหน่วยงาน" })}</b>
        &nbsp; ({intl.formatMessage({ defaultMessage: "ผู้สร้างคำสั่งซื้อ" })})
      </MyTypography>
      <form>
        <Grid container key={JSON.stringify(formik.values)}>
          <Grid item xs={12} md={6}>
            <Box className={classes.box}>
              <Grid container spacing={3}>
                <Grid item xs={5} className={classes.rightGrid}>
                  <MyTypography variant="body3">
                    {intl.formatMessage({
                      defaultMessage: "Contract / PI No."
                    })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <MyTypography variant="body3" className={classes.piNo}>
                    {dataExportOrder?.pi?.code}
                  </MyTypography>
                </Grid>
                <Grid item xs={5} className={classes.rightGrid}>
                  <MyTypography variant="body3">
                    {intl.formatMessage({ defaultMessage: "Order Type" })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <TextInputDisable
                    value={
                      orderTypeChoices.find(
                        item => item.value === formik.values.orderType
                      )?.label
                    }
                  />
                  {/* <SingleAutocomplete
                    name="orderType"
                    placeholder={"Select"}
                    getOptionLabel={option => option.label}
                    options={orderTypeChoices}
                    value={formik.values.orderType}
                    renderOption={(option, { selected }) => (
                      <span>
                        <MyTypography
                          variant={"body3"}
                          className={classNames(classes.selectItem, {
                            [classes.selected]: selected
                          })}
                        >
                          {option.value === ScgExportOrderType.ZOR ? (
                            <>
                              <span>{ScgExportOrderType.ZOR}</span>
                              <span style={{ fontWeight: 400 }}>
                                {" "}
                                (Standard Order)
                              </span>
                            </>
                          ) : (
                            option.label
                          )}
                        </MyTypography>
                      </span>
                    )}
                    onChange={formik.handleChange}
                  /> */}
                </Grid>
                <Grid item xs={5} className={classes.rightGrid}>
                  <MyTypography variant="body3">
                    {intl.formatMessage({
                      defaultMessage: "Sales Organization"
                    })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <TextInputDisable
                    value={
                      saleOrganizationChoices?.find(
                        item => item.value === formik.values.salesOrganizationId
                      )?.label
                    }
                  />
                  {/*  <SingleAutocomplete
                    name="salesOrganizationId"
                    placeholder={"Select"}
                    getOptionLabel={option => option.label}
                    options={saleOrganizationChoices}
                    value={formik.values.salesOrganizationId}
                    onChange={formik.handleChange}
                  /> */}
                </Grid>
                <Grid item xs={5} className={classes.rightGrid}>
                  <MyTypography variant="body3">
                    {intl.formatMessage({
                      defaultMessage: "Distribution Channel"
                    })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <TextInputDisable
                    value={
                      distributionChannelChoicesFinal?.find(
                        item =>
                          item.value === formik.values.distributionChannelId
                      )?.label
                    }
                  />
                  {/* <SingleAutocomplete
                    placeholder={"Select"}
                    getOptionLabel={option => option.label}
                    options={distributionChannelChoicesFinal}
                    name="distributionChannelId"
                    value={formik.values.distributionChannelId}
                    onChange={formik.handleChange}
                  /> */}
                </Grid>
                <Grid item xs={5} className={classes.rightGrid}>
                  <MyTypography variant="body3">
                    {intl.formatMessage({ defaultMessage: "Division" })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <TextInputDisable
                    value={
                      divisionChoices?.find(
                        item => item.value === formik.values.divisionId
                      )?.label
                    }
                  />
                  {/* <SingleAutocomplete
                    placeholder={"Select"}
                    getOptionLabel={option => option.label}
                    options={divisionChoices}
                    name="divisionId"
                    value={formik.values.divisionId}
                    onChange={formik.handleChange}
                  /> */}
                </Grid>
                <Grid item xs={5} className={classes.rightGrid}>
                  <MyTypography variant="body3">
                    {intl.formatMessage({ defaultMessage: "Sales Office" })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <SingleAutocomplete
                    placeholder={"Select"}
                    getOptionLabel={option => option.label || ""}
                    options={saleOfficeChoices}
                    name="sales-office-id"
                    id="sales-office-id"
                    value={formik.values.salesOfficeId}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid item xs={5} className={classes.rightGrid}>
                  <MyTypography variant="body3">
                    {intl.formatMessage({ defaultMessage: "Sales Group" })}
                  </MyTypography>
                </Grid>
                <Grid item xs={7}>
                  <SingleAutocomplete
                    placeholder={"Select"}
                    getOptionLabel={option => option.label || ""}
                    options={saleGroupChoices}
                    name="sales-group-id"
                    id="sales-group-id"
                    value={formik.values.salesGroupId}
                    onChange={formik.handleChange}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default AgencyTab;
